import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [gallery] - The horizontally scrolling stage (tp / IT#16845).
 * @property {HTMLButtonElement} [zoomToggle] - The magnifier toggle (tp / IT#16845).
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    // tp / IT#16845: Seit die Buehne horizontal blaettert, scrollt nicht mehr der
    // <dialog>, sondern die <ul>. scroll blubbert nicht — ohne diesen zweiten
    // Listener bliebe das aktive Thumbnail beim Blaettern stehen.
    this.refs.gallery?.addEventListener('scroll', this.handleScroll);
    this.#updateNavState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.gallery?.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      this.#updateNavState(index);
      this.#updateZoomToggle(index);
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
    this.#updateNavState(index);
    this.#updateZoomToggle(index);
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.#resetZoomExcept(activeIndex);
    this.#updateNavState(activeIndex);
    this.#updateZoomToggle(activeIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    // tp / IT#16845: Blaettern per Tastatur — ohne das waere die Lightbox auf dem
    // Desktop nur mit der Maus bedienbar.
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      return void this.next();
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      return void this.previous();
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles a click on the stage.
   *
   * Der Klick schliesst nur, wenn die Flaeche NEBEN dem Bild getroffen wurde —
   * auf dem Bild selbst sitzt die Lupe. `event.target` ist hier auf das
   * gebundene <li> gepatcht (siehe component.js), der echte Ursprung steckt
   * deshalb in composedPath()[0].
   *
   * @param {PointerEvent} event - The pointer event.
   */
  handleStageClick(event) {
    const origin = event.composedPath?.()[0];
    const wrapper =
      origin instanceof Element
        ? origin.closest('.product-media-container')?.querySelector('drag-zoom-wrapper')
        : null;

    // Das <img> fuellt die ganze Buehne; der leere Rand daneben gehoert zur
    // Buehne, nicht zum Bild. Deshalb nach Geometrie entscheiden, nicht nach
    // dem getroffenen Element.
    if (wrapper && /** @type {any} */ (wrapper).containsPoint?.(event.clientX, event.clientY)) return;

    this.close();
  }

  /**
   * Scrolls to the previous media item.
   */
  async previous() {
    await this.#step(-1);
  }

  /**
   * Scrolls to the next media item.
   */
  async next() {
    await this.#step(1);
  }

  /**
   * Moves the gallery by the given offset.
   * @param {number} offset - How many items to move.
   */
  async #step(offset) {
    const { media } = this.refs;
    const current = media.indexOf(await getMostVisibleElement(media));
    const target = current + offset;

    if (target < 0 || target >= media.length) return;

    this.#resetZoomExcept(target);
    this.selectThumbnail(target, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
    this.#updateNavState(target);
    this.#updateZoomToggle(target);
  }

  /**
   * Toggles the magnifier on the media that is currently on stage.
   */
  async toggleZoom() {
    const wrapper = await this.#activeWrapper();

    if (!wrapper?.canZoom) return;

    wrapper.toggleZoom();
    this.refs.zoomToggle?.setAttribute('aria-pressed', `${wrapper.isZoomed}`);
  }

  /**
   * The drag-zoom wrapper of the media currently on stage.
   * @returns {Promise<import('./drag-zoom-wrapper').DragZoomWrapper | null>}
   */
  async #activeWrapper() {
    const { media } = this.refs;
    const active = await getMostVisibleElement(media);

    return active?.querySelector('drag-zoom-wrapper') ?? null;
  }

  /**
   * Puts every stage back to the full image — a zoom left standing on the
   * previous slide would otherwise greet the next one half-panned.
   * @param {number} [keepIndex] - Index that may keep its zoom.
   */
  #resetZoomExcept(keepIndex) {
    this.refs.media.forEach((item, index) => {
      if (index === keepIndex) return;

      const wrapper = item.querySelector('drag-zoom-wrapper');

      if (wrapper && 'resetZoom' in wrapper) /** @type {any} */ (wrapper).resetZoom();
    });
  }

  /**
   * Shows the magnifier toggle only where the source file actually has pixels
   * left to show.
   * @param {number} [index] - The active index.
   */
  async #updateZoomToggle(index) {
    const { zoomToggle } = this.refs;

    if (!zoomToggle) return;

    const wrapper =
      typeof index === 'number'
        ? this.refs.media[index]?.querySelector('drag-zoom-wrapper')
        : await this.#activeWrapper();

    // canZoom braucht die gemessene Anzeigebreite — ein Frame nach showModal().
    // Die Quellbreite selbst kommt aus data-source-width und ist sofort da.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const canZoom = Boolean(wrapper && /** @type {any} */ (wrapper).canZoom);

    zoomToggle.toggleAttribute('hidden', !canZoom);
    zoomToggle.setAttribute('aria-pressed', `${Boolean(wrapper && /** @type {any} */ (wrapper).isZoomed)}`);
  }

  /**
   * Hides the arrow that would run past the end of the gallery.
   * @param {number} [index] - The active index, if already known.
   */
  #updateNavState(index) {
    const { media } = this.refs;
    const prev = this.querySelector('.dialog-zoomed-gallery__nav--prev');
    const next = this.querySelector('.dialog-zoomed-gallery__nav--next');

    if (!prev || !next) return;

    if (media.length < 2) {
      prev.toggleAttribute('hidden', true);
      next.toggleAttribute('hidden', true);
      return;
    }

    const active = typeof index === 'number' ? index : 0;

    prev.toggleAttribute('hidden', active <= 0);
    next.toggleAttribute('hidden', active >= media.length - 1);
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
